import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/**
 * Cart item interface
 */
export interface CartItem {
    productId: string;
    productName: string;
    productImage: string;
    price: number;
    originalPrice?: number;
    discountAmount?: number;
    saleName?: string;
    quantity: number;
    maxQuantity: number;
    availableStockInventory?: number;
    categoryName: string;
    sku?: string | undefined;
    variantId?: string | undefined;
    colorName?: string | null | undefined;
    sizeName?: string | null | undefined;
}

/**
 * Cart state interface
 */
interface CartState {
    items: CartItem[];
    totalItems: number;
    subtotal: number;
    discount: number;
    couponCode: string | null;
    isLoading: boolean;
    error: string | null;
}

/**
 * Get initial cart state from localStorage
 */
const getInitialState = (): CartState => {
    try {
        const savedCart = localStorage.getItem('cart');
        if (savedCart) {
            const parsed = JSON.parse(savedCart) as CartState;
            return {
                ...parsed,
                isLoading: false,
                error: null,
            };
        }
    } catch {
        // Invalid cart data, use default
    }

    return {
        items: [],
        totalItems: 0,
        subtotal: 0,
        discount: 0,
        couponCode: null,
        isLoading: false,
        error: null,
    };
};

/**
 * Calculate cart totals
 */
const calculateTotals = (items: CartItem[]) => {
    const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
    const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

    return { totalItems, subtotal };
};

/**
 * Save cart to localStorage
 */
const saveCart = (state: CartState) => {
    try {
        localStorage.setItem(
            'cart',
            JSON.stringify({
                items: state.items,
                totalItems: state.totalItems,
                subtotal: state.subtotal,
                discount: state.discount,
                couponCode: state.couponCode,
            })
        );
    } catch {
        // Storage full or unavailable
    }
};

/**
 * Cart slice for shopping cart state management
 */
const cartSlice = createSlice({
    name: 'cart',
    initialState: getInitialState(),
    reducers: {
        /**
         * Add item to cart
         */
        addToCart: (state, action: PayloadAction<CartItem>) => {
            const existingIndex = state.items.findIndex(
                (item) => item.productId === action.payload.productId && item.variantId === action.payload.variantId
            );

            if (existingIndex >= 0) {
                const existingItem = state.items[existingIndex];
                if (existingItem) {
                    const newQuantity = existingItem.quantity + action.payload.quantity;
                    existingItem.quantity = Math.min(newQuantity, existingItem.maxQuantity);
                }
            } else {
                state.items.push(action.payload);
            }

            const totals = calculateTotals(state.items);
            state.totalItems = totals.totalItems;
            state.subtotal = totals.subtotal;

            saveCart(state);
        },

        /**
         * Update item quantity
         */
        updateQuantity: (
            state,
            action: PayloadAction<{ productId: string; variantId?: string | undefined; quantity: number }>
        ) => {
            const { productId, variantId, quantity } = action.payload;
            const item = state.items.find((i) => i.productId === productId && i.variantId === variantId);

            if (item) {
                item.quantity = Math.max(1, Math.min(quantity, item.maxQuantity));

                const totals = calculateTotals(state.items);
                state.totalItems = totals.totalItems;
                state.subtotal = totals.subtotal;

                saveCart(state);
            }
        },

        /**
         * Remove item from cart
         */
        removeFromCart: (state, action: PayloadAction<{ productId: string; variantId?: string | undefined } | string>) => {
            if (typeof action.payload === 'string') {
                state.items = state.items.filter((item) => item.productId !== action.payload);
            } else {
                const { productId, variantId } = action.payload;
                state.items = state.items.filter(
                    (item) => !(item.productId === productId && item.variantId === variantId)
                );
            }

            const totals = calculateTotals(state.items);
            state.totalItems = totals.totalItems;
            state.subtotal = totals.subtotal;

            saveCart(state);
        },

        /**
         * Clear entire cart
         */
        clearCart: (state) => {
            state.items = [];
            state.totalItems = 0;
            state.subtotal = 0;
            state.discount = 0;
            state.couponCode = null;

            localStorage.removeItem('cart');
        },

        /**
         * Apply coupon code
         */
        applyCoupon: (
            state,
            action: PayloadAction<{ code: string; discount: number }>
        ) => {
            state.couponCode = action.payload.code;
            state.discount = action.payload.discount;

            saveCart(state);
        },

        /**
         * Remove coupon
         */
        removeCoupon: (state) => {
            state.couponCode = null;
            state.discount = 0;

            saveCart(state);
        },

        /**
         * Set loading state
         */
        setCartLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },

        /**
         * Set error state
         */
        setCartError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
            state.isLoading = false;
        },

        /**
         * Sync cart from server
         */
        syncCart: (state, action: PayloadAction<CartItem[]>) => {
            state.items = action.payload;

            const totals = calculateTotals(state.items);
            state.totalItems = totals.totalItems;
            state.subtotal = totals.subtotal;

            saveCart(state);
        },
    },
});

export const {
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    applyCoupon,
    removeCoupon,
    setCartLoading,
    setCartError,
    syncCart,
} = cartSlice.actions;

export default cartSlice.reducer;
